import { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Tooltip } from 'ui/tooltip';
import { Button } from 'ui/button';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button/constants';
import { ModalFooter, ModalBody, ModalHeader, Modal, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { useModalContext } from 'utils/contexts/modal';
import { useSubmitProjectSettings } from 'modules/projectSettings/hooks/useSubmitProjectSettings';
import { ProjectSettingsFormSchemaType } from 'modules/projectSettings/contexts';
import { FIELD_NAME_LABELS } from 'modules/projectSettings/constants';
import { Body2, spacing } from 'ui/theme';

const Body2NoMargin = styled(Body2)`
  margin-bottom: 0;
`;

const SpacedDiscList = styled.ul`
  list-style-type: disc;
  width: 100%;
  max-height: ${spacing.x3};
  margin-top: ${spacing.half};
`;

export const UNSAVED_CHANGES_TEST_IDS = {
  ProjectSettingsUnsavedChangesModalTestId: 'project-settings-unsaved-changes-modal',
  ProjectSettingsUnsavedChangesModalLeaveButtonTestId:
    'project-settings-unsaved-changes-modal-leave-button',
  ProjectSettingsUnsavedChangesModalSaveButtonTestId:
    'project-settings-unsaved-changes-modal-save-button',
  ProjectSettingsUnsavedChangesModalUnsavedChangesNames:
    'project-settings-unsaved-changes-modal-unsaved-changes-names',
};

export const UNSAVED_CHANGES_MODAL_TEXT = {
  modalHeaderUnsavedText: 'Unsaved changes',
  modalBodyText: 'The following settings have been updated. Do you want to save your changes?',
  modalLeaveWithoutSavingText: 'Leave without saving',
  modalSaveAndExitText: 'Save and exit',
  disabledTooltipText:
    'Please address the persisting errors on the page prior to attempting to save',
};

export type UnsavedChangesModalProps = {
  onConfirm: () => void;
};

export const UnsavedChangesModal = ({ onConfirm }: UnsavedChangesModalProps) => {
  const { hideModal } = useModalContext<string>();
  const { isLoading, onSubmitWithCallback } = useSubmitProjectSettings();

  const {
    formState: { dirtyFields, errors },
  } = useFormContext<ProjectSettingsFormSchemaType>();
  const hasFEFormErrors = !isEmpty(errors);

  const unsavedChangesList = useMemo(
    () =>
      Object.keys(dirtyFields).map((field) => (
        <li key={field}>{FIELD_NAME_LABELS[field as keyof typeof FIELD_NAME_LABELS]}</li>
      )),
    [dirtyFields],
  );

  const onClose = () => {
    onConfirm();
    hideModal();
  };
  const onSave = onSubmitWithCallback(onClose, hideModal);

  return (
    <Modal
      withPortal
      onClose={hideModal}
      isModalOpen={true}
      id={UNSAVED_CHANGES_TEST_IDS.ProjectSettingsUnsavedChangesModalTestId}
      data-test={UNSAVED_CHANGES_TEST_IDS.ProjectSettingsUnsavedChangesModalTestId}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader autoFocus={false}>
        {UNSAVED_CHANGES_MODAL_TEXT.modalHeaderUnsavedText}
      </ModalHeader>
      <ModalBody>
        <Body2NoMargin>{UNSAVED_CHANGES_MODAL_TEXT.modalBodyText}</Body2NoMargin>
        {unsavedChangesList.length > 0 && (
          <SpacedDiscList
            data-test={
              UNSAVED_CHANGES_TEST_IDS.ProjectSettingsUnsavedChangesModalUnsavedChangesNames
            }
          >
            {unsavedChangesList}
          </SpacedDiscList>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={UNSAVED_CHANGES_TEST_IDS.ProjectSettingsUnsavedChangesModalLeaveButtonTestId}
          onClick={onClose}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.SECONDARY}
          isDisabled={isLoading}
        >
          {UNSAVED_CHANGES_MODAL_TEXT.modalLeaveWithoutSavingText}
        </Button>
        <Tooltip
          isUsedOnButton
          isEnabled={hasFEFormErrors}
          tooltipContent={UNSAVED_CHANGES_MODAL_TEXT.disabledTooltipText}
        >
          <Button
            data-test={UNSAVED_CHANGES_TEST_IDS.ProjectSettingsUnsavedChangesModalSaveButtonTestId}
            onClick={onSave}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
            isDisabled={hasFEFormErrors}
            isLoading={isLoading}
          >
            {UNSAVED_CHANGES_MODAL_TEXT.modalSaveAndExitText}
          </Button>
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
};
